/**
 * @fileoverview Inbox navigation and chat selection handlers.
 * @module pages/chat/useChatNavigationHandlers
 */
import { useCallback, useMemo } from "react";
import {
  extractMessageTimestamp,
  maybeMarkChatAsRead,
  handleChatSelect,
} from "../../logic/chat/index.js";
import { CHAT_BACK_TRANSITION_MS } from "../../utils/chatUiConstants.js";
import { openSimulatedChat, resetChatSelection } from "./chatSelectionHelpers.js";

/** Returns memoized inbox and selection handlers. */
const useChatNavigationHandlers = (ctx) => {
  const resetSelection = useCallback(() => {
    resetChatSelection(ctx);
  }, [ctx]);

  const handleSearch = useCallback(
    (event) => ctx.setSearchQuery(event.target.value),
    [ctx.setSearchQuery],
  );

  const handleViewChange = useCallback(
    (view) => ctx.setActiveView(view),
    [ctx.setActiveView],
  );

  const handleChatSelection = useCallback(
    (chat) => {
      handleChatSelect({
        chat,
        users: ctx.users,
        companies: ctx.companies,
        navigate: ctx.navigate,
        setLoading: ctx.setLoading,
        setSelectedChat: ctx.setSelectedChat,
        setSelectedUser: ctx.setSelectedUser,
        setSelectedCompany: ctx.setSelectedCompany,
        setChatMessages: ctx.setChatMessages,
        setChats: ctx.setChats,
        extractMessageTimestampFn: extractMessageTimestamp,
        maybeMarkChatAsReadFn: maybeMarkChatAsRead,
        isUserActiveRef: ctx.isUserActiveRef,
        lastSeenMessageTimestampByChatRef: ctx.lastSeenMessageTimestampByChatRef,
        messageEndRef: ctx.messageEndRef,
      });
      if (ctx.isCompactViewport) {
        ctx.setShowChatInTablet(true);
      }
    },
    [
      ctx.users,
      ctx.companies,
      ctx.navigate,
      ctx.isCompactViewport,
      ctx.setLoading,
      ctx.setSelectedChat,
      ctx.setSelectedUser,
      ctx.setSelectedCompany,
      ctx.setChatMessages,
      ctx.setChats,
      ctx.isUserActiveRef,
      ctx.lastSeenMessageTimestampByChatRef,
      ctx.messageEndRef,
      ctx.setShowChatInTablet,
    ],
  );

  const handleBackToInbox = useCallback(() => {
    ctx.setShowChatInTablet(false);
    setTimeout(resetSelection, CHAT_BACK_TRANSITION_MS);
  }, [ctx.setShowChatInTablet, resetSelection]);

  const clearReadOnlyChat = useCallback(() => {
    ctx.setShowChatInTablet(false);
    resetSelection();
  }, [ctx.setShowChatInTablet, resetSelection]);

  const handleSimulatedChatOpen = useCallback(
    (request) => {
      openSimulatedChat(ctx, request);
    },
    [ctx],
  );

  const onBackToInbox = useMemo(
    () => (ctx.isCompactViewport ? handleBackToInbox : null),
    [ctx.isCompactViewport, handleBackToInbox],
  );

  return {
    handleSearch,
    handleViewChange,
    handleChatSelection,
    handleBackToInbox,
    clearReadOnlyChat,
    handleSimulatedChatOpen,
    onBackToInbox,
  };
};

export default useChatNavigationHandlers;
